import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { PageShell, PageHero } from "../components/PageShell";
import { CtaBanner } from "../components/CtaBanner";
import { futureRoles, EMAIL } from "../data/content";

export default function CareerDetail() {
  const { slug } = useParams();
  const role = futureRoles.find((item) => item.slug === slug);

  if (!role) {
    return (
      <PageShell>
        <section className="mx-auto max-w-4xl px-4 py-28 sm:px-6 lg:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400">Not found</p>
          <h1 className="mt-5 font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl">
            This opportunity is not open right now.
          </h1>
          <p className="mt-5 max-w-xl text-base text-slate-400">
            We may still be exploring future opportunities in this area. Please return to the careers overview and choose another path or send your profile directly.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              to="/careers"
              className="spark-gradient inline-flex items-center gap-2 rounded-full px-6 py-3 font-display text-sm font-semibold text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to careers
            </Link>
          </div>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div data-testid="career-detail-page">
        <PageHero
          overline="Career opportunity"
          title={role.title}
          description={role.description}
        />

        <section className="py-20 lg:py-28">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-8 lg:grid-cols-[1.3fr_0.7fr]">
              <div className="card-gradient rounded-2xl border border-white/[0.08] p-8 sm:p-10">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-400">Role overview</p>
                <h2 className="mt-5 font-display text-2xl font-semibold text-white sm:text-3xl">
                  What we are looking for.
                </h2>
                <p className="mt-5 text-base leading-relaxed text-slate-300">
                  {role.idealFor}
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                  {role.focusAreas.map((point) => (
                    <span key={point} className="chip">
                      {point}
                    </span>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-white/[0.08] bg-void p-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Current status</p>
                <ul className="mt-6 space-y-4">
                  <li className="flex items-start gap-3 text-sm text-slate-300">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" />
                    <span>No formal openings are currently listed, but we are always open to strong profiles.</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-12 flex flex-wrap gap-4">
              <Link
                to="/careers"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 font-display text-sm font-semibold text-slate-200 transition-colors duration-300 hover:border-cyan-400/50 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 text-cyan-400" />
                Back to careers
              </Link>
              <a
                href={`mailto:${EMAIL}?subject=${encodeURIComponent(`Career Profile — ${role.title} — Spark Link IT Solutions`)}`}
                className="spark-gradient inline-flex items-center gap-2 rounded-full px-6 py-3 font-display text-sm font-semibold text-white"
              >
                Send your profile
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>

        {/* <CtaBanner
          title="Interested in this role?"
          text="Send your profile and we will get in touch if there is a fit for future opportunities or upcoming openings."
        /> */}
      </div>
    </PageShell>
  );
}
